import categoryService from '../../services/tickets/category.service.js'

import entityUtility from '../../utils/entity.utility.js'
import httpUtility from '../../utils/http.utility.js'
import iconUtility from '../../utils/icon.utility.js'

import { useToast } from '../../composables/useToast.js'

import { t } from '@/i18n'

const toast = useToast()

async function saveCategory(scope, payload) {
  try {
    let response
    if (payload._id) response = await categoryService.update.category(scope, payload._id, payload)
    else response = await categoryService.create.category(scope, payload)
    return response.data.item
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.settings.errors.saveCategory')))
    return null
  }
}

async function deleteCategory(scope, id) {
  try {
    await categoryService.delete.category(scope, id)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.settings.errors.deleteItem')))
    return false
  }
}

function mapCategories(array) {
  return array.map((category) => ({
    value: category._id,
    label: category.title,
    color: category.color,
    iconName: category.icon?.name ?? '',
    icon: iconUtility.get(category.icon?.name ?? ''),
  }))
}

function findCategory(id, array) {
  return entityUtility.find(id, array)
}

class CategoryController {
  /**
   * Map each category to `{ value, label, color, iconName, icon }`.
   *
   * @param {object[]} array
   * @returns {Array<{ value: string, label: string, color?: string, iconName: string, icon: import('vue').Component }>}
   */
  map(array) {
    return mapCategories(array)
  }

  /**
   * Look up one category by `id` in `array`. Returns the row, or `null`
   * when `id` is missing or not found.
   *
   * @param {string|null|undefined} id
   * @param {Array<{ _id: string }>} array
   * @returns {object|null}
   */
  find(id, array) {
    return findCategory(id, array)
  }

  save = {
    /**
     * Create or update a category row. Branches on `payload._id`
     * (truthy → update; omitted → create). Returns the saved category on
     * success, `null` on failure (toasts the error).
     *
     * @param {string} scope - Settings API URL prefix (`ticket-account` or `ticket-group/:id`).
     * @param {object} payload
     * @returns {Promise<object|null>}
     */
    async category(scope, payload) {
      return saveCategory(scope, payload)
    },
  }

  delete = {
    /**
     * Delete a category row. Returns `true` on success, `false` on
     * failure (toasts the error).
     *
     * @param {string} scope - Settings API URL prefix (`ticket-account` or `ticket-group/:id`).
     * @param {string} id
     * @returns {Promise<boolean>}
     */
    async category(scope, id) {
      return deleteCategory(scope, id)
    },
  }
}

export default new CategoryController()
